/**
 * Labor Breakdown Component
 * Displays NECA labor unit estimation with expandable step details
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Copy, Package, Lightbulb } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/LaborBreakdown.css';

interface AssemblyStepMatch {
  step_number: number;
  step_description: string;
  quantity: number | null;
  quantity_unit: string | null;
  matched_component: string;
  measurement: string;
  normal_labor_units: number | null;
  difficult_labor_units: number | null;
  very_difficult_labor_units: number | null;
  unit: string;
  section: string | null;
  page_number: number | null;
  notes: string | null;
  // Material scraping fields
  material_cost?: number | null;
  material_product_title?: string | null;
  material_product_url?: string | null;
}

// Matched assembly with reasoning (from RSMeans lookup)
interface MatchedAssemblyInfo {
  assembly: {
    name: string;
    components: Array<{
      quantity: number;
      quantityUnit: string;
      description: string;
    }>;
  };
  quantity: number;
  page?: number;
  reasoning?: string;
}

interface LaborBreakdownProps {
  estimate: {
    project_description: string;
    steps: AssemblyStepMatch[];
    total_labor_normal: number;
    total_labor_difficult: number;
    total_labor_very_difficult: number;
    matched_assemblies?: MatchedAssemblyInfo[];
  };
  onStepExpand: (pageNumber: number) => void;
  laborRate?: number;
  materialCosts?: Map<string, number>;
  defaultOverhead?: number;
  defaultProfit?: number;
}

export const LaborBreakdown: React.FC<LaborBreakdownProps> = ({
  estimate,
  onStepExpand,
  laborRate = 75,
  materialCosts = new Map(),
  defaultOverhead = 15,
  defaultProfit = 10
}) => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [expandedAssembly, setExpandedAssembly] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [enrichedSteps, setEnrichedSteps] = useState<AssemblyStepMatch[]>(estimate.steps);
  const [rowSettings, setRowSettings] = useState<Map<string, { laborRate: number; overhead: number; profit: number }>>(new Map());
  
  // Fetch material data from database to enrich steps
  useEffect(() => {
    const fetchMaterialData = async () => {
      try {
        const { data, error } = await supabase
          .from('estimate_labor_steps')
          .select('matched_component, measurement, material_cost, material_product_title, material_product_url');
        
        if (!error && data) {
          // Create a map of material data by component+measurement
          const materialData = new Map();
          data.forEach((row: any) => {
            const key = `${row.matched_component}|${row.measurement}`;
            materialData.set(key, {
              material_cost: row.material_cost ? Number(row.material_cost) : null,
              material_product_title: row.material_product_title,
              material_product_url: row.material_product_url
            });
          });
          
          // Enrich steps with material data
          const enriched = estimate.steps.map(step => {
            const key = `${step.matched_component}|${step.measurement}`;
            const material = materialData.get(key);
            
            if (material && material.material_cost) {
              return {
                ...step,
                material_cost: material.material_cost,
                material_product_title: material.material_product_title,
                material_product_url: material.material_product_url
              };
            }
            return step;
          });
          
          setEnrichedSteps(enriched);
          console.log('✅ Enriched steps with material data');
        }
      } catch (error) {
        console.error('Error fetching material data:', error);
      }
    };
    
    fetchMaterialData();
    
    // Subscribe to realtime updates for material cost changes
    const channel = supabase
      .channel('material-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'estimate_labor_steps'
      }, () => {
        console.log('🔄 Material data updated, refetching...');
        fetchMaterialData();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [estimate.steps]);
  
  // Format number with commas
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Get row-specific settings or defaults
  const getRowSettings = (stepKey: string) => {
    return rowSettings.get(stepKey) || { laborRate, overhead: defaultOverhead, profit: defaultProfit };
  };
  
  // Update row setting
  const updateRowSetting = (stepKey: string, field: 'laborRate' | 'overhead' | 'profit', value: number) => {
    const current = rowSettings.get(stepKey) || { laborRate, overhead: defaultOverhead, profit: defaultProfit };
    setRowSettings(new Map(rowSettings.set(stepKey, { ...current, [field]: value })));
  };
  
  // Copy table to clipboard with HTML formatting
  const copyTableToClipboard = async () => {
    const headers = ['Component', 'Quantity', 'NECA 1', 'Labor $', 'Material Price', 'Material $', 'Total'];
    const rows = enrichedSteps.map(step => {
      const hours = calculateStepTotal(step);
      const key = `${step.matched_component}-${step.measurement}`;
      const settings = getRowSettings(key);
      const materialUnitCost = step.material_cost || materialCosts.get(key) || 0;
      const materialTotal = (step.quantity || 0) * materialUnitCost;
      
      const laborCost = hours * settings.laborRate;
      const subtotal = laborCost + materialTotal;
      const overheadAmount = subtotal * (settings.overhead / 100);
      const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
      const stepTotal = subtotal + overheadAmount + profitAmount;
      
      return [
        `${step.matched_component} (${step.measurement})`,
        `${step.quantity} ${step.quantity_unit}`,
        `${hours.toFixed(2)} hrs`,
        `$${formatCurrency(laborCost)}`,
        `$${materialUnitCost.toFixed(2)}`,
        `$${formatCurrency(materialTotal)}`,
        `$${formatCurrency(stepTotal)}`
      ];
    });
    
    const grandTotalHours = (estimate.total_labor_normal * 0.80);
    const grandTotalLaborCost = enrichedSteps.reduce((sum, step) => {
      const hours = calculateStepTotal(step);
      const key = `${step.matched_component}-${step.measurement}`;
      const settings = getRowSettings(key);
      return sum + (hours * settings.laborRate);
    }, 0);
    const grandTotalMaterialCost = enrichedSteps.reduce((sum, step) => {
      const materialUnitCost = step.material_cost || materialCosts.get(`${step.matched_component}-${step.measurement}`) || 0;
      return sum + ((step.quantity || 0) * materialUnitCost);
    }, 0);
    const grandTotal = enrichedSteps.reduce((sum, step) => {
      const hours = calculateStepTotal(step);
      const key = `${step.matched_component}-${step.measurement}`;
      const settings = getRowSettings(key);
      const materialUnitCost = step.material_cost || materialCosts.get(key) || 0;
      const materialTotal = (step.quantity || 0) * materialUnitCost;
      const laborCost = hours * settings.laborRate;
      const subtotal = laborCost + materialTotal;
      const overheadAmount = subtotal * (settings.overhead / 100);
      const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
      return sum + (subtotal + overheadAmount + profitAmount);
    }, 0);
    
    const totalRow = [
      'TOTAL',
      '',
      `${grandTotalHours.toFixed(1)} hrs`,
      `$${formatCurrency(grandTotalLaborCost)}`,
      '',
      `$${formatCurrency(grandTotalMaterialCost)}`,
      `$${formatCurrency(grandTotal)}`
    ];
    
    // Create HTML table for rich formatting (black text, black borders)
    const htmlTable = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; font-family: Inter, -apple-system, sans-serif; border: 1px solid black;">
        <thead>
          <tr>
            ${headers.map((h, i) => {
              let align = 'left';
              if (i === 1 || i === 2) align = 'center';
              if (i >= 3) align = 'right';  // All columns from Labor $ onwards are right-aligned
              return `<th style="padding: 10px; text-align: ${align}; font-weight: 600; border: 1px solid black; color: black;">${h}</th>`;
            }).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map(row => `
            <tr>
              ${row.map((cell, i) => {
                let align = 'left';
                if (i === 1 || i === 2) align = 'center';
                if (i >= 3) align = 'right';  // All columns from Labor $ onwards are right-aligned
                return `<td style="padding: 10px; text-align: ${align}; border: 1px solid black; color: black;">${cell}</td>`;
              }).join('')}
            </tr>
          `).join('')}
          <tr>
              ${totalRow.map((cell, i) => {
                let align = 'left';
                if (i === 1 || i === 2) align = 'center';
                if (i >= 3) align = 'right';  // All columns from Labor $ onwards are right-aligned
              return `<td style="padding: 12px; text-align: ${align}; font-weight: 700; border: 1px solid black; color: black;"><strong>${cell}</strong></td>`;
            }).join('')}
          </tr>
        </tbody>
      </table>
    `;
    
    // Create plain text fallback (TSV)
    const tsv = [
      headers.join('\t'),
      ...rows.map(row => row.join('\t')),
      totalRow.join('\t')
    ].join('\n');
    
    try {
      // Write both HTML and plain text to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlTable], { type: 'text/html' }),
          'text/plain': new Blob([tsv], { type: 'text/plain' })
        })
      ]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback to plain text only
      try {
        await navigator.clipboard.writeText(tsv);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err2) {
        console.error('Fallback copy also failed:', err2);
      }
    }
  };
  
  // Calculate labor hours for a step (NECA 1 baseline)
  const calculateStepTotal = (step: AssemblyStepMatch) => {
    const calculate = (qty: number | null, units: number | null, unit: string) => {
      if (!qty || !units) return 0;
      
      const unitType = unit.toUpperCase();
      let baseHours = 0;
      if (unitType === 'M') baseHours = (qty / 1000) * units;
      else if (unitType === 'C') baseHours = (qty / 100) * units;
      else baseHours = qty * units;
      
      // Use baseline NECA values directly (no discount applied)
      return baseHours;
    };
    
    return calculate(step.quantity, step.normal_labor_units, step.unit);
  };
  
  // Toggle step expansion and navigate PDF
  const toggleStep = (stepNumber: number, pageNumber: number | null) => {
    // Always navigate to PDF when clicking a step
    if (pageNumber) {
      onStepExpand(pageNumber);
    }
    
    // Toggle expansion - only one step open at a time
    if (expandedStep === stepNumber) {
      setExpandedStep(null); // Close if clicking on already open step
    } else {
      setExpandedStep(stepNumber); // Open clicked step, closes any other
    }
  };
  
  // Get unit type label
  const getUnitLabel = (unit: string) => {
    const unitType = unit.toUpperCase();
    if (unitType === 'M') return 'per 1,000';
    if (unitType === 'C') return 'per 100';
    return 'each';
  };
  
  return (
    <div className="labor-breakdown">
      {/* Assemblies Used Section - Shows reasoning for each assembly */}
      {estimate.matched_assemblies && estimate.matched_assemblies.length > 0 && (
        <div className="assemblies-section" style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)',
          borderRadius: '12px',
          border: '1px solid #d1fae5'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.875rem'
          }}>
            <Package size={18} style={{ color: 'var(--green-text)' }} />
            <h4 style={{ margin: 0, color: 'var(--green-text-dark)', fontSize: '0.9375rem', fontWeight: 600 }}>
              Assemblies Used ({estimate.matched_assemblies.length})
            </h4>
          </div>
          
          {estimate.matched_assemblies.map((assembly, idx) => {
            const isExpanded = expandedAssembly === assembly.assembly.name;
            
            return (
              <div 
                key={idx}
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '8px',
                  marginBottom: idx < estimate.matched_assemblies!.length - 1 ? '0.75rem' : 0,
                  border: '1px solid var(--border-color)',
                  overflow: 'hidden'
                }}
              >
                {/* Assembly Header */}
                <div 
                  onClick={() => setExpandedAssembly(isExpanded ? null : assembly.assembly.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.25rem'
                    }}>
                      <span style={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem', 
                        color: 'var(--text-primary)' 
                      }}>
                        {assembly.assembly.name}
                      </span>
                      <span style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        background: 'var(--bg-tertiary)',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '4px'
                      }}>
                        Qty: {assembly.quantity}
                      </span>
                      {assembly.page && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'var(--green-text)',
                          background: 'var(--green-tint-strong)',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px'
                        }}>
                          pg. {assembly.page}
                        </span>
                      )}
                    </div>
                    
                    {/* Reasoning - always visible */}
                    {assembly.reasoning && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.375rem',
                        marginTop: '0.5rem',
                        padding: '0.5rem 0.625rem',
                        background: 'var(--yellow-tint-medium)',
                        borderRadius: '6px',
                        border: '1px solid #fde047'
                      }}>
                        <Lightbulb size={14} style={{ color: '#ca8a04', flexShrink: 0, marginTop: '1px' }} />
                        <span style={{
                          fontSize: '0.8125rem',
                          color: '#713f12',
                          lineHeight: '1.4'
                        }}>
                          {assembly.reasoning}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginLeft: '0.5rem', color: 'var(--text-placeholder)' }}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                
                {/* Expanded Components List */}
                {isExpanded && (
                  <div style={{
                    padding: '0 1rem 0.75rem 1rem',
                    borderTop: '1px solid var(--border-subtle)'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      fontWeight: 500,
                      marginBottom: '0.5rem',
                      marginTop: '0.75rem'
                    }}>
                      COMPONENTS INCLUDED:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {assembly.assembly.components.map((comp, compIdx) => (
                        <div 
                          key={compIdx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.375rem 0.5rem',
                            background: 'var(--bg-secondary)',
                            borderRadius: '4px',
                            fontSize: '0.8125rem'
                          }}
                        >
                          <span style={{ color: 'var(--text-secondary)' }}>{comp.description}</span>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                            {comp.quantity} {comp.quantityUnit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Individual Step Cards - Citation Style */}
      {enrichedSteps.map(step => {
        const isExpanded = expandedStep === step.step_number;
        const total = calculateStepTotal(step);
        
        return (
          <div 
            key={step.step_number} 
            className="labor-step"
            onClick={() => toggleStep(step.step_number, step.page_number)}
          >
            {/* Step Header */}
            <div className="step-header">
              <span className="step-number">
                Step {step.step_number}:
              </span>
              <span className="step-description">
                {step.step_description}
              </span>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            
            {/* Expanded Details - List Format */}
            {isExpanded && (
              <div className="step-details">
                <table className="step-detail-table">
                  <tbody>
                    <tr>
                      <td className="detail-label">Component</td>
                      <td className="detail-value">{step.matched_component}</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Size</td>
                      <td className="detail-value">{step.measurement}</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Quantity</td>
                      <td className="detail-value">{step.quantity} {step.quantity_unit}</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Unit</td>
                      <td className="detail-value">{step.unit} ({getUnitLabel(step.unit)})</td>
                    </tr>
                    <tr>
                      <td className="detail-label">Labor Units (Normal)</td>
                      <td className="detail-value-hours">{step.normal_labor_units?.toFixed(2) || 0} units</td>
                    </tr>
                    <tr>
                      <td className="detail-label">NECA 1</td>
                      <td className="detail-value-hours">{(step.normal_labor_units || 0).toFixed(2)} units</td>
                    </tr>
                    {/* Material Cost Row */}
                    {step.material_cost && (
                      <tr>
                        <td className="detail-label">Material Cost</td>
                        <td className="detail-value">${step.material_cost.toFixed(2)} per unit</td>
                      </tr>
                    )}
                    <tr className="totals-separator">
                      <td className="detail-label">Step Total</td>
                      <td className="detail-value-hours"><strong>{total.toFixed(2)} hrs</strong></td>
                    </tr>
                  </tbody>
                </table>
                
                {/* Material Product Link */}
                {step.material_product_url && (
                  <a 
                    href={step.material_product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '12px',
                      marginBottom: '8px',
                      padding: '0',
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontSize: '13px',
                      transition: 'color 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#3b82f6';
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>🛒</span>
                    <span style={{ fontSize: '13px' }}>Material: {step.material_product_title}</span>
                  </a>
                )}
                
                <div className="step-meta" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <BookOpen size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span>{step.matched_component} | {step.measurement} | Page {step.page_number}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
      
      {/* Total Summary - Full Table with Math */}
      <div className="labor-totals">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <h4 style={{ margin: 0 }}>Estimate Table</h4>
          <button
            onClick={copyTableToClipboard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              background: copySuccess ? '#10b981' : 'var(--bg-primary)',
              color: copySuccess ? 'var(--bg-primary)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
            onMouseEnter={(e) => {
              if (!copySuccess) {
                e.currentTarget.style.background = 'var(--bg-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-strong)';
              }
            }}
            onMouseLeave={(e) => {
              if (!copySuccess) {
                e.currentTarget.style.background = 'var(--bg-primary)';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }
            }}
          >
            <Copy size={14} />
            {copySuccess ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <div className="totals-table-wrapper">
          <table className="totals-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Quantity</th>
                <th>NECA 1</th>
                <th style={{ textAlign: 'right' }}>Labor $</th>
                <th style={{ textAlign: 'right' }}>Material Price</th>
                <th style={{ textAlign: 'right' }}>Material $</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {enrichedSteps.map(step => {
                const hours = calculateStepTotal(step);
                const key = `${step.matched_component}-${step.measurement}`;
                const settings = getRowSettings(key);
                const materialUnitCost = step.material_cost || materialCosts.get(key) || 0;
                const materialTotal = (step.quantity || 0) * materialUnitCost;
                
                const laborCost = hours * settings.laborRate;
                const subtotal = laborCost + materialTotal;
                const overheadAmount = subtotal * (settings.overhead / 100);
                const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
                const stepTotal = subtotal + overheadAmount + profitAmount;
                
                return (
                  <tr key={step.step_number}>
                    <td className="step-name">
                      <div style={{ fontWeight: 700, fontSize: '0.8125rem' }}>{step.matched_component}</div>
                      <div style={{ color: 'var(--text-placeholder)', fontSize: '0.75rem', marginTop: '0.125rem' }}>{step.measurement}</div>
                    </td>
                    <td className="hours-value" style={{ textAlign: 'center' }}>{step.quantity} {step.quantity_unit}</td>
                    <td className="hours-value" style={{ textAlign: 'center' }}>{hours.toFixed(2)} hrs</td>
                    <td className="hours-value" style={{ textAlign: 'right' }}>${formatCurrency(laborCost)}</td>
                    <td className="hours-value" style={{ textAlign: 'right' }}>
                      ${materialUnitCost.toFixed(2)}
                    </td>
                    <td className="hours-value" style={{ textAlign: 'right' }}>
                      ${formatCurrency(materialTotal)}
                    </td>
                    <td className="hours-value" style={{ textAlign: 'right' }}><strong>${formatCurrency(stepTotal)}</strong></td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td></td>
                <td style={{ textAlign: 'center' }}><strong>{(estimate.total_labor_normal * 0.80).toFixed(2)} hrs</strong></td>
                <td style={{ textAlign: 'right' }}><strong>${formatCurrency(enrichedSteps.reduce((sum, step) => {
                  const hours = calculateStepTotal(step);
                  const key = `${step.matched_component}-${step.measurement}`;
                  const settings = getRowSettings(key);
                  return sum + (hours * settings.laborRate);
                }, 0))}</strong></td>
                <td style={{ textAlign: 'right' }}></td>
                <td style={{ textAlign: 'right' }}><strong>${formatCurrency(enrichedSteps.reduce((sum, step) => {
                  const materialUnitCost = step.material_cost || materialCosts.get(`${step.matched_component}-${step.measurement}`) || 0;
                  return sum + ((step.quantity || 0) * materialUnitCost);
                }, 0))}</strong></td>
                <td style={{ textAlign: 'right' }}><strong>${formatCurrency(enrichedSteps.reduce((sum, step) => {
                  const hours = calculateStepTotal(step);
                  const key = `${step.matched_component}-${step.measurement}`;
                  const settings = getRowSettings(key);
                  const materialUnitCost = step.material_cost || materialCosts.get(key) || 0;
                  const materialTotal = (step.quantity || 0) * materialUnitCost;
                  const laborCost = hours * settings.laborRate;
                  const subtotal = laborCost + materialTotal;
                  const overheadAmount = subtotal * (settings.overhead / 100);
                  const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
                  return sum + (subtotal + overheadAmount + profitAmount);
                }, 0))}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


